/* eslint-disable no-restricted-globals */
import { Suspense, lazy, useContext, useEffect } from "react";
import styled from "styled-components";
import CustomSpinner from "./components/spinner";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
  useLocation,
} from "react-router-dom";
import { AuthContext } from "./contexts/auth-context";
import { ColorContext } from "./contexts/color-context/ColorContext";
import Header from "./components/header";
import { lightenColor, lightenColorSoft, tintColor, getContrastColor, hexToRgbChannels } from "./utils";
import Footer from "./components/footer";
import ChooseFederation from "./components/FederationTiles";
import NewSidebar from "./components/NewSideBar";
import Orbit from "./components/HomePage/Orbit";
import EntryHub from "./components/HomePage/EntryHub";
import styles from "./routes.module.css";
import {
  useSidebar,
  SidebarProvider,
} from "../src/components/NewSideBar/SidebarContext";
import { BreadcrumbProvider } from "../src/components/Breadcrumb/BreadcrumbContext";
import SubHeader from "./components/SubHeader";
import FederationSelection from "./components/FederationSelection";
import IdentitiesAttributes from "./components/IdentitiesAttributes";
import PastQueries from "./components/PastQueries";
import ProvenanceRecord from "./components/ProvenanceRecord";
import NewQuery from "./components/NewQuery";
import DiscoverAi from "./components/AskTamarai";
import ActivationChannels from "./components/ActivationChannels";
import ActivationForm from "./components/ActivationForm";
import ActivationHistory from "./components/ActivationHistory/index.";
import { useAsyncFn } from "./hooks/use-async-fn";
import DatasetManager from "./components/DatasetManager";
import DatasetCreatePage from "./components/DatasetManager/create";
import NestedQueries from "./components/NestedQueries";
import ManagementDashboardRoutes from "./components/ManagementDashboard/ManagementDashboardRoutes";
import OrganizationDashboardRoutes from "./components/OrganizationDashboard/OrganizationDashboardRoutes";
import SystemHealth from "./components/SystemHealth";
import { OnamLanding } from "./onam/hub/OnamHub";
import OnamHeader from "./onam/chrome/OnamHeader";
import OnamFooter from "./onam/chrome/OnamFooter";
import { isOnamChrome } from "./onam/paths";
import { canCurate, isCurationPath, CURATION_DENIED } from "./onam/access";
import { FEATURES } from "./onam/features";
import {
  FederationProvider,
  useFederation,
} from "./components/FederationSelection/FederationContext";



// ONAM loads only when someone opens it, so Curation users don't download it
const OnamRoutes = lazy(() => import("./onam"));

export const paths = {
  home: "/home",
  entryHub: "/entry-hub",
  chooseFederation: "/choose-federation",
  federationSelection: "/federation-selection",
  identitiesAttributes: "/campaign-planning",
  campaignActivation: "/campaign-activation",
  organizationSelection: "/organization-selection",
  pastQueries: "/campaign-reports",
  discoverAi: "/ask-tamarai",
  provRecord: "/provenance-record",
  newQuery: "/new-query",
  activationChannels: "/activation-channels",
  activationForm: "/activation-form",
  defaultMatchrate: "/default-matchrate",
  activationHistory: "/activation-history",
  datasetManager: "/dataset-manager",
  createDataset: "/create-dataset",
  nestedQueries: "/nested-queries",
  profileSearch: "/ ",
  globalSchema: "/global-schema",
  provenanceTrail: "/trails",
  markedResults: "/marked-results",
  activatedProfiles: "/  ",
  audiencegeneration: "/       ",
  dataManager: "/data-manager",
  managementDashboard: "/management-dashboard",
  managementDashboardDetails: "/management-dashboard/details",
  organizationDashboard: "/organization-dashboard",
  destinations: "/destinations",
  configure: "/destinations/configure",
  systemHealth: "/system-health",
  // ONAM: Engage > Advertising (src/onam). Everything under /onam belongs to it.
  onam: "/onam",
};

const Fallback = () => <CustomSpinner />;

const Layout = ({ authContext, children, colorContext }) => {
  const { getApplication } = authContext;
  const federation = useFederation();

  const getApplicationFn = useAsyncFn({
    promiseFn: getApplication,
  });

  useEffect(() => {
    const colorScheme =
      getApplicationFn.data?.setting?.toLowerCase() === "federation settings"
        ? federation?.branding
        : getApplicationFn.data?.color_scheme;

    if (colorScheme?.element_color) {
      const element_color = colorScheme.element_color;
      const primary_color = colorScheme.primary_color;
      const secondary_color = colorScheme.secondary_color;
      const info_color = lightenColor(secondary_color);
      const light_primary_color = lightenColor(element_color);
      const soft_primary_color = lightenColorSoft(element_color);

      document.documentElement.style.setProperty(
        "--Primary-Color-Shade-1",
        element_color,
      );
      document.documentElement.style.setProperty(
        "--Primary-Color-Shade-1-pale",
        lightenColor(element_color),
      );
      document.documentElement.style.setProperty(
        "--Primary-Color-Shade-1-pale-hover",
        tintColor(element_color, 0.88),
      );
      document.documentElement.style.setProperty(
        "--Primary-Color-Shade-1-rgb",
        hexToRgbChannels(element_color),
      );
      document.documentElement.style.setProperty(
        "--breadcrumb-text-color",
        getContrastColor(element_color),
      );
      document.documentElement.style.setProperty(
        "--active-bg-color",
        primary_color,
      );
      document.documentElement.style.setProperty(
        "--accent-color",
        secondary_color,
      );
      document.documentElement.style.setProperty("--info-color", info_color);
      document.documentElement.style.setProperty(
        "--light-primary-color",
        light_primary_color,
      );
      document.documentElement.style.setProperty(
        "--soft-primary-color",
        soft_primary_color,
      );
    }
  }, [federation.branding, getApplicationFn.data]);

  const location = useLocation();
  // the way in (/home, /entry-hub) and ONAM wear the ONAM header and footer. Curation keeps MAP's,
  // unless the experimental ONAM skin is on (FEATURES.curationSkin): then it wears ONAM's chrome too,
  // and body.onam-skin restyles its pages (src/onam/skin; styling only)
  const skin = FEATURES.curationSkin && !isOnamChrome(location.pathname);
  const onamChrome = isOnamChrome(location.pathname) || skin;
  useEffect(() => {
    if (skin) import("./onam/skin").catch(() => {});
    document.body.classList.toggle("onam-skin", skin);
  }, [skin]);
  const colorContextLocal = useContext(ColorContext);
  const { setFedPlatformName } = colorContextLocal;
  // Define routes where Sidebar should NOT be shown
  const newUIRoutes = [paths.home.trim(), paths.entryHub.trim()];

  // ONAM brings its own section nav, so MAP's sidebar is hidden there too
  const hideSideNav =
    newUIRoutes.includes(location.pathname) ||
    location.pathname.startsWith(paths.onam);
  const { isSubSidebarCollapsed } = useSidebar();
  // ColorProvider state: set after render, not during it
  useEffect(() => {
    if (hideSideNav) {
      setFedPlatformName(null);
    }
  }, [hideSideNav]); // eslint-disable-line react-hooks/exhaustive-deps
  return authContext.isAuth ? (
    <div className={styles.layout}>
      {!hideSideNav && <NewSidebar authContext={authContext} />}

      <div className={styles.mainWrapper}>
        {onamChrome ? (
          <>
            {/* MAP's header stays mounted, hidden, for the session state it writes */}
            <div className="onam-maphead">
              <Header authContext={authContext} />
            </div>
            <OnamHeader />
          </>
        ) : (
          <Header authContext={authContext} />
        )}
        {!hideSideNav && (
          <SubHeader isSubSidebarCollapsed={isSubSidebarCollapsed} />
        )}

        <div
          className={
            !hideSideNav
              ? `${styles.mainContent} ${
                  isSubSidebarCollapsed ? styles.collapsed : styles.expanded
                }`
              : styles.mainContent
          }
        >
          {children}
        </div>

        {onamChrome ? (
          <OnamFooter />
        ) : (
          <Footer
            authContext={authContext}
            fullYear={new Date().getFullYear()}
            hideSideNav={hideSideNav}
          />
        )}
      </div>
    </div>
  ) : (
    <>{children}</>
  );
};
const LowercaseRedirect = () => {
  const location = useLocation();

  useEffect(() => {
    const lower = location.pathname.toLowerCase();

    if (location.pathname !== lower) {
      window.history.replaceState(null, "", lower);
    }
  }, [location]);

  return null;
};

const RouterNode = () => {
  const authContext = useContext(AuthContext);
  const { isAuthPending, user, appMetadata, rbacAccessDenied, clearAuth } = authContext;

  useEffect(() => {
    const handlePageShow = () => {
      if (!isAuthPending && !user) {
        window.location.replace("/home");
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("popstate", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("popstate", handlePageShow);
    };
  }, [isAuthPending, user]);

  const RouteList: ReadonlyArray<any> = [
    {
      path: paths.home,
      component: () => <Orbit authContext={authContext} />,
      isPrivate: false,
    },
    {
      path: paths.entryHub,
      component: () => <EntryHub authContext={authContext} />,
      isPrivate: false,
    },
    {
      path: paths.chooseFederation,
      component: () => <ChooseFederation authContext={authContext} />,
      isPrivate: true,
    },
    {
      path: paths.nestedQueries,
      component: () => <NestedQueries />,
      isPrivate: true,
    },
    {
      path: paths.federationSelection,
      component: () => <FederationSelection authContext={authContext} />,
      isPrivate: false,
    },
    {
      path: paths.identitiesAttributes,
      component: () => <IdentitiesAttributes />,
      isPrivate: true,
      permissions: {
        campaignplanning: ["read"],
      },
    },
    // { Commenting for future use -- create federation page
    //   path: paths.organizationSelection,
    //   component: OrganizationContainer,
    //   isPrivate: true,
    // },
    {
      path: paths.pastQueries,
      component: () => <PastQueries />,
      isPrivate: true,
      permissions: {
        query: ["read", "write", "full"],
      },
    },
    {
      path: paths.provRecord,
      component: () => <ProvenanceRecord />,
      isPrivate: true,
      permissions: {
        provenancetrail: ["read"],
      },
    },
    {
      path: paths.newQuery,
      component: () => <NewQuery />,
      isPrivate: true,
      permissions: {
        query: ["write", "full"],
      },
    },
    {
      path: paths.discoverAi,
      component: () => <DiscoverAi />,
      isPrivate: true,
      permissions: {
        query: ["read", "write", "full"],
      },
    },

    {
      path: paths.activationChannels,
      component: () => <ActivationChannels authContext={authContext} />,
      isPrivate: true,
      permissions: {
        activationconnectors: ["read", "full"],
      },
    },
    {
      path: paths.activationForm,
      component: () => <ActivationForm />,
      isPrivate: true,
      permissions: {
        activationconnectors: ["full"],
      },
    },
    {
      path: paths.activationHistory,
      component: () => <ActivationHistory />,
      isPrivate: true,
      permissions: {
        activationhistory: ["full"],
      },
    },
    {
      path: paths.datasetManager,
      component: () => <DatasetManager />,
      isPrivate: true,
      permissions: {
        datamanager: ["read", "full", "write"],
      },
    },
    {
      path: paths.createDataset,
      component: () => <DatasetCreatePage />,
      isPrivate: true,
      permissions: {
        datamanager: ["full", "write"],
      },
    },
    {
      path: paths.managementDashboard,
      component: () => <ManagementDashboardRoutes />,
      isPrivate: true,
      exact: false,
    },
    {
      path: paths.organizationDashboard,
      component: () => <OrganizationDashboardRoutes />,
      isPrivate: true,
      exact: false,
    },
    {
      path: paths.systemHealth,
      component: () => <SystemHealth />,
      isPrivate: true,
    },
    {
      path: paths.onam,
      component: () => (
        <Suspense fallback={<OnamLanding />}>
          <OnamRoutes />
        </Suspense>
      ),
      isPrivate: true,
      exact: false,
    },
  ];
  // if (MAINTENANCE_MODE) {
  //   return <MaintenancePage />;
  // }

  if (!user && !isAuthPending) {
    window.location.replace("/home");
    return null;
  }

  if (rbacAccessDenied) {
    return (
      <RbacDeniedOverlay>
        <RbacDeniedCard>
          <RbacDeniedHeader>
            <RbacDeniedHeaderIcon>⚠</RbacDeniedHeaderIcon>
            Access Denied
          </RbacDeniedHeader>
          <RbacDeniedBody>
            <RbacDeniedMessage>
              This user does not have access to the application. Contact the admin for more details.
            </RbacDeniedMessage>
          </RbacDeniedBody>
          <RbacDeniedFooter>
            <RbacDeniedLogoutBtn onClick={clearAuth}>Logout</RbacDeniedLogoutBtn>
          </RbacDeniedFooter>
        </RbacDeniedCard>
      </RbacDeniedOverlay>
    );
  }

  return !appMetadata || isAuthPending ? (
    <Fallback />
  ) : (
    <Router>
      <LowercaseRedirect />
      <FederationProvider>
        <SidebarProvider>
          <BreadcrumbProvider>
            <Layout authContext={authContext} colorContext={ColorContext}>
              <Suspense fallback={<Fallback />}>
                <Switch>
                  {/* Curation is limited to platform admins (src/onam/access.ts): everyone else goes back to the hub */}
                  {!canCurate(authContext) ? (
                    <Route
                      path={RouteList.map((r) => r.path).filter(isCurationPath)}
                      render={() => (
                        <Redirect to={{ pathname: paths.entryHub, state: { onamNotice: CURATION_DENIED } }} />
                      )}
                    />
                  ) : null}
                  {RouteList.map((curr) =>
                    curr.isPrivate ? (
                      <PrivateRoute
                        key={curr.path}
                        path={curr.path}
                        component={curr.component}
                        permissions={curr.permissions}
                        authContext={authContext}
                        exact={curr.exact !== false}
                      />
                    ) : (
                      <PublicRoute
                        key={curr.path}
                        path={curr.path}
                        authContext={authContext}
                        colorContext={ColorContext}
                        component={curr.component}
                        exact={true}
                      />
                    ),
                  )}
                  <Redirect from="/" to={paths.home} />
                </Switch>
              </Suspense>
            </Layout>
          </BreadcrumbProvider>
        </SidebarProvider>
      </FederationProvider>{" "}
    </Router>
  );
};

const PrivateRoute = ({
  component: Component,
  authContext,
  permissions,
  ...rest
}) => {
  const storedPermissions = JSON.parse(
    localStorage.getItem("userPermissions") || "{}",
  );
  console.log({ storedPermissions });
  let hasPermission = true;

  if (permissions) {
    hasPermission = Object.entries(
      permissions as Record<string, string[]>,
    ).every(([moduleKey, allowedLevels]) =>
      allowedLevels.includes(storedPermissions?.[moduleKey]),
    );
  }

  const shouldRedirect = !authContext.isAuth || !hasPermission;

  return (
    <Route
      {...rest}
      render={(props) =>
        shouldRedirect ? (
          <Redirect
            to={{
              pathname: paths.home,
              state: { from: props.location },
            }}
          />
        ) : (
          <Component {...props} authContext={authContext} />
        )
      }
    />
  );
};

const PublicRoute = ({
  component: Component,
  authContext,

  ...rest
}) => (
  <Route
    {...rest}
    render={(props) => <Component {...props} authContext={authContext} />}
  />
);

export default RouterNode;

const RbacDeniedOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const RbacDeniedCard = styled.div`
  background: white;
  border-radius: 12px;
  width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow: hidden;

  @keyframes rbac-slide-in {
    from {
      opacity: 0;
      transform: translateY(-16px) scale(0.96);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  animation: rbac-slide-in 0.22s ease-out;
`;

const RbacDeniedHeader = styled.div`
  background: #dc2626;
  color: white;
  padding: 16px 24px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  text-align: center;
  font-family: "Montserrat", sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const RbacDeniedHeaderIcon = styled.span`
  font-size: 16px;
`;

const RbacDeniedBody = styled.div`
  padding: 28px 32px 20px;
`;

const RbacDeniedMessage = styled.p`
  color: #4b5563;
  font-size: 14px;
  line-height: 1.65;
  margin: 0;
  text-align: center;
`;

const RbacDeniedFooter = styled.div`
  padding: 4px 32px 28px;
  display: flex;
  justify-content: center;
`;

const RbacDeniedLogoutBtn = styled.button`
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 11px 48px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.4px;
  transition: background 0.15s, transform 0.1s;

  &:hover {
    background: #b91c1c;
  }

  &:active {
    transform: scale(0.98);
  }
`;
